import React, { useState } from "react";
import { Check, ChevronsUpDown, MapPin } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

/**
 * SearchableSelect
 * options: [{ value: string, label: string, hint?: string }]
 * placeholder: shown when no value picked
 * emptyLabel: shown when search yields no match
 * disabled: disables the trigger
 * testId: base data-testid for the trigger
 * icon: optional lucide icon component
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Select…",
  emptyLabel = "No matches.",
  disabled = false,
  disabledHint,
  testId,
  icon: Icon = MapPin,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid={testId}
          disabled={disabled}
          className={`inline-flex items-center gap-2 h-10 w-full min-w-0 rounded-md border border-[#E6E4DF] bg-white px-3 text-sm text-left transition-colors ${
            disabled ? "opacity-60 cursor-not-allowed" : "hover:border-[#1C3F35] cursor-pointer"
          } ${className}`}
        >
          {Icon && <Icon className="w-4 h-4 shrink-0 text-[#5E5E5E]" />}
          <span className={`flex-1 truncate ${selected ? "text-[#1A1A1A]" : "text-[#7A8C77]"}`}>
            {disabled && disabledHint ? disabledHint : (selected ? selected.label : placeholder)}
          </span>
          <ChevronsUpDown className="w-4 h-4 shrink-0 text-[#5E5E5E]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[min(24rem,90vw)] bg-white"
        data-testid={testId ? `${testId}-popover` : undefined}
      >
        <Command>
          <CommandInput placeholder="Search…" className="h-10" />
          <CommandList className="max-h-72">
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={`${opt.label} ${opt.value}`}
                  data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
                  onSelect={() => {
                    onChange(opt.value === value ? "" : opt.value);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2"
                >
                  <Check
                    className={`w-4 h-4 ${opt.value === value ? "opacity-100 text-[#1C3F35]" : "opacity-0"}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{opt.label}</div>
                    {opt.hint && (
                      <div className="text-[10px] uppercase tracking-widest text-[#7A8C77] font-bold">{opt.hint}</div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
